import { useCallback, useEffect, useState } from "react";

/**
 * THE COACH TYPING (front door, 2026-09-20).
 *
 * The coach's words arrive from the server whole; this reveals them the way
 * somebody writes them. It is the only animation on this page that touches
 * TEXT, so it obeys three rules:
 *
 *   1. NOTHING IS EVER HIDDEN. The revealed prefix always ends at the whole
 *      text, and a press anywhere in the conversation finishes it at once
 *      (`finish`).
 *   2. WHAT WAS ALREADY THERE IS NOT REPLAYED. Whatever a mount opens on,
 *      the conversation the server hands back on arrival (`markTyped`), and
 *      anything this tab has already watched are all drawn whole. Only words
 *      that ARRIVE type. The one exception is the coach's introduction to a
 *      new learner (`typeOnMount`, AJ intake 2026-09-24): it is the first
 *      thing the page shows and it is the coach speaking first, so it types
 *      on the mount that opens on it, once.
 *   3. STILLNESS IS RESPECTED. `prefers-reduced-motion` means the whole text,
 *      immediately.
 *
 * A PARAGRAPH IS A BREATH. When the reveal reaches a blank line between
 * paragraphs it rests for a moment before the next one starts (AJ intake,
 * 2026-09-24: the introduction is five paragraphs and reads as speech, not
 * as a wall). The rest is part of the reveal, so a press finishes it too.
 *
 * HOW MUCH IS SHOWN IS KEPT WITH THE KEY IT BELONGS TO, and that is the
 * whole design. The first version kept a bare count, so the render on which
 * a new reply arrived measured the OLD count against the NEW text: a reply
 * shorter than the message before it looked complete for one render, was
 * marked as already typed, and then never revealed a character — the coach's
 * answer rendered as nothing at all, live, on the second question anybody
 * asked (found on production 2026-09-21). A count that cannot be read against
 * the wrong words cannot do that.
 */

/** About ninety characters a second: brisk, and still ahead of reading speed. */
const TICK_MS = 33;
const CHARS_PER_TICK = 3;
/** The rest at a paragraph break, in ticks: about two thirds of a second. Counted in ticks rather than on the clock so that a batch of ticks run together (React under a test's fake timers) still rests and then goes on. */
export const PARAGRAPH_PAUSE_TICKS = 20;
const PARAGRAPH_BREAK = "\n\n";

/**
 * What this browser tab has already watched being typed, by key. A module
 * value on purpose: it must outlive the front door being unmounted and
 * mounted again as the learner moves around the app, and it must not outlive
 * the tab — nothing here is worth storing.
 */
const alreadyTyped = new Set<string>();

/**
 * These were said before this page was looking: the conversation the server
 * hands back when the front door opens. Without this the newest line of a
 * kept conversation types itself out again on every visit, which is the coach
 * repeating yesterday's answer as though it were new.
 */
export function markTyped(keys: readonly string[]): void {
  for (const key of keys) alreadyTyped.add(key);
}

/** The opposite, for a page that must watch an utterance again: the dev review's switch between states, and the tests. */
export function forgetTyped(keys: readonly string[]): void {
  for (const key of keys) alreadyTyped.delete(key);
}

export interface Typing {
  /** The text as far as it has been revealed. The whole of it when nothing is typing. */
  readonly shown: string;
  readonly typing: boolean;
  /** Reveal the rest at once. */
  readonly finish: () => void;
}

export interface TypewriterOptions {
  /** This key types even on the mount that opens on it, unless it has been watched already. */
  readonly typeOnMount?: boolean;
}

interface Progress {
  readonly key: string | null;
  readonly shown: number;
  /** Ticks still to rest at a paragraph break, or 0. */
  readonly rest: number;
}

/**
 * @param key   Identity of this utterance (a message id, an opening's stamp).
 *              Null when the coach has said nothing yet.
 * @param text  The whole of what the coach said.
 */
export function useTypewriter(key: string | null, text: string, options: TypewriterOptions = {}): Typing {
  const typeOnMount = options.typeOnMount ?? false;
  const [progress, setProgress] = useState<Progress>(() => {
    // Whatever this mount opens on is already on the page: it never types,
    // except the coach's introduction, which is the page speaking first.
    if (key !== null && typeOnMount && !alreadyTyped.has(key) && !stillness()) return { key, shown: 0, rest: 0 };
    if (key !== null) alreadyTyped.add(key);
    return { key, shown: text.length, rest: 0 };
  });

  /** Where this key starts from, for a render whose state belongs to another one. */
  const from = key === null || alreadyTyped.has(key) || stillness() ? text.length : 0;
  const shown = Math.min(progress.key === key ? progress.shown : from, text.length);
  const done = shown >= text.length;

  // Adopt the new key. Rendering already uses `from`, so this settles the
  // state without a frame of the wrong words.
  useEffect(() => {
    setProgress((current) => (current.key === key ? current : { key, shown: from, rest: 0 }));
  }, [key, from]);

  useEffect(() => {
    if (key === null || done) return;
    const tick = setInterval(() => {
      setProgress((current) => {
        const at = current.key === key ? current.shown : from;
        if (current.key === key && current.rest > 0) return { key, shown: at, rest: current.rest - 1 };
        // Reveal up to the next paragraph break, then rest on it.
        const window = text.slice(at, at + CHARS_PER_TICK);
        const breakAt = window.indexOf(PARAGRAPH_BREAK);
        if (breakAt >= 0 && at + breakAt + PARAGRAPH_BREAK.length < text.length) {
          return { key, shown: at + breakAt + PARAGRAPH_BREAK.length, rest: PARAGRAPH_PAUSE_TICKS };
        }
        return { key, shown: Math.min(at + CHARS_PER_TICK, text.length), rest: 0 };
      });
    }, TICK_MS);
    return () => {
      clearInterval(tick);
    };
  }, [key, text, from, done]);

  useEffect(() => {
    if (key !== null && done) alreadyTyped.add(key);
  }, [key, done]);

  const finish = useCallback(() => {
    setProgress({ key, shown: text.length, rest: 0 });
  }, [key, text]);

  return { shown: text.slice(0, shown), typing: !done, finish };
}

function stillness(): boolean {
  return globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}
